import type { LearnTopic } from "../topic-types";

/**
 * Complex PTSD (C-PTSD) — flagship module.
 *
 * Educational content only. This is not a diagnostic tool. Add new
 * guides, exercises, prompts or worksheets by appending to a section's
 * `resources` array.
 */
export const CPTSD_TOPIC: LearnTopic = {
  slug: "cptsd",
  emoji: "🌊",
  title: "Complex PTSD (C-PTSD)",
  tagline: "Understanding complex trauma",
  theme: "sky",
  category: "cptsd-responses",
  cardBlurb: "What C-PTSD is, how it can develop, and gentle ways to recognise your own patterns.",
  isNew: true,
  seoDescription:
    "A trauma-informed, educational guide to Complex PTSD (C-PTSD): what it is, how it can develop, common experiences, and gentle practices for recognising and working with your own patterns. Not a diagnostic tool.",
  intro: [
    "Complex PTSD (C-PTSD) is a way of describing the lasting impact that repeated or prolonged difficult experiences can have on a person's sense of safety, emotions, relationships and self-worth. It is recognised by the World Health Organization in the ICD-11 as a distinct diagnosis, related to but different from PTSD.",
    "This module is educational. It is designed to help you understand complex trauma in plain English, notice patterns you might recognise in yourself, and explore some gentle, practical tools. It is not a diagnostic tool, a substitute for therapy, or a checklist to self-diagnose from — only a qualified professional (such as a GP, psychiatrist or trauma-informed therapist) can assess or diagnose C-PTSD, PTSD, or any other condition.",
    "You may recognise a great deal of what follows, some of it, or none at all — and any of those is a completely valid response. There is no correct way to relate to this material, and you are welcome to go at whatever pace feels manageable, including not at all today.",
  ],
  closing:
    "This module offers education and gentle self-reflection tools — it cannot assess, diagnose or treat C-PTSD or any other condition. If any of this content brings up more than feels manageable, please be gentle with yourself and consider reaching out to a GP, a trauma-informed therapist, or a crisis service. Our Crisis Resources page is always here if you need it.",
  continueTo: [
    { label: "Emotional Flashbacks", to: "/learn/emotional-flashbacks" },
    { label: "Trauma Responses", to: "/learn/trauma-responses" },
    { label: "Window of Tolerance", to: "/learn/window-of-tolerance" },
    { label: "Journal", to: "/journal" },
    { label: "Crisis Resources", to: "/crisis" },
  ],
  sections: [
    {
      slug: "understand",
      emoji: "📚",
      title: "Understand",
      blurb: "What C-PTSD is, how it differs from PTSD, and how complex trauma can develop.",
      intro:
        "Plain-English education first. Nothing here asks you to look inward yet — you can simply read, and come back to the rest whenever you're ready.",
      resources: [
        {
          slug: "what-is-cptsd",
          kind: "learn",
          title: "What is Complex PTSD?",
          summary: "An educational overview of C-PTSD and where the term comes from.",
          minutes: 5,
          blocks: [
            {
              type: "prose",
              text: "Complex PTSD (C-PTSD) is a term used to describe a cluster of difficulties that can develop after repeated or prolonged traumatic experiences — often ones where escape felt difficult or impossible, such as ongoing childhood adversity, domestic abuse, captivity, trafficking, or long-term neglect. It was formally added to the World Health Organization's ICD-11 in 2019 as a diagnosis distinct from, but related to, PTSD.",
            },
            {
              type: "prose",
              text: "The word 'complex' does not mean 'more severe' or 'more complicated to deal with.' It refers to the nature of the trauma itself — repeated and prolonged, often relational, often beginning early in life — and to the wider range of effects it can have beyond the core features of PTSD.",
            },
            {
              type: "note",
              text: "This page is educational. Only a qualified professional — such as a GP, psychiatrist or clinical psychologist — can assess or diagnose C-PTSD, PTSD, or any other mental health condition. Recognising yourself in a description here is not the same as having that diagnosis.",
            },
            {
              type: "list",
              title: "According to the ICD-11, C-PTSD involves the core features of PTSD, plus difficulties in three further areas",
              items: [
                "Emotional regulation — big feelings that arrive fast, or numbness that makes feelings hard to reach at all",
                "Self-concept — persistent beliefs of being worthless, defeated, or diminished, often carrying shame or guilt",
                "Relationships — difficulty feeling close to others, or sustaining closeness once it's there",
              ],
            },
            {
              type: "quote",
              text: "Complex trauma responses are not a character flaw. They are a nervous system and a set of beliefs that adapted to survive something difficult, and haven't yet been told it's safe to stand down.",
            },
          ],
        },
        {
          slug: "ptsd-vs-cptsd",
          kind: "learn",
          title: "PTSD vs C-PTSD",
          summary: "How the two are related, and what tends to set them apart.",
          minutes: 5,
          blocks: [
            {
              type: "prose",
              text: "PTSD and C-PTSD share a core set of features — re-experiencing (such as flashbacks or intrusive memories), avoidance, and a heightened sense of current threat. PTSD can develop after a single traumatic event, such as an accident, assault or natural disaster, though it can also follow repeated events.",
            },
            {
              type: "prose",
              text: "C-PTSD includes those same core features, but also involves broader, more pervasive difficulties with emotional regulation, self-concept and relationships. These tend to be linked to trauma that was repeated or prolonged, often occurred in relationships where the person depended on or couldn't easily escape the person causing harm, and often began in childhood — though it can also develop in adulthood.",
            },
            {
              type: "list",
              title: "A rough comparison — not a checklist for self-diagnosis",
              items: [
                "PTSD: often linked to a single event or a defined period of events",
                "C-PTSD: often linked to repeated or prolonged experiences, frequently starting young",
                "PTSD: core features are re-experiencing, avoidance, and hypervigilance",
                "C-PTSD: those core features, plus difficulties regulating emotion, a diminished sense of self-worth, and relationship difficulties",
                "Both: entirely valid, real, and treatable with appropriate professional support",
              ],
            },
            {
              type: "note",
              text: "These categories exist to help clinicians communicate and to guide treatment — not to rank suffering or to sort people into boxes. Many people notice a mix of features, and that is common.",
            },
          ],
        },
        {
          slug: "how-complex-trauma-develops",
          kind: "learn",
          title: "How complex trauma can develop",
          summary: "Repetition, prolonged exposure, entrapment, and why childhood matters.",
          minutes: 6,
          blocks: [
            {
              type: "prose",
              text: "Complex trauma tends to be associated less with a single frightening event and more with an ongoing situation — one where difficult or harmful experiences happened repeatedly over time, and where leaving, escaping or being protected wasn't a realistic option.",
            },
            {
              type: "list",
              title: "Circumstances often associated with complex trauma",
              items: [
                "Childhood abuse, neglect, or growing up with a caregiver who was frightening, unpredictable or unavailable",
                "Domestic abuse or coercive control within a relationship",
                "Ongoing bullying, harassment or discrimination",
                "Captivity, trafficking, or war and conflict",
                "Repeated medical trauma, particularly in childhood",
                "Any situation where harm was repeated and escape felt impossible",
              ],
            },
            {
              type: "prose",
              text: "Childhood is a particularly sensitive period because a developing nervous system and sense of self are being shaped in relationship with caregivers. When safety, comfort and consistency are missing during those years, a child may adapt in ways that helped them survive then, but which can continue to run in the background long into adulthood.",
            },
            {
              type: "prose",
              text: "Entrapment is a key idea here. When a person cannot physically leave, cannot emotionally leave (because they depend on or love the person involved), or cannot see a way out, the body and mind often shift from short bursts of fight-or-flight into longer-term states of hypervigilance, shutdown, or fawning — because those short-term responses aren't sustainable over months or years.",
            },
            {
              type: "note",
              text: "Not everyone exposed to these circumstances develops C-PTSD, and people can develop it after experiences not listed here. Trauma responses depend on many factors, including support available at the time, age, temperament and other resources — none of which are anyone's fault.",
            },
          ],
        },
      ],
    },
    {
      slug: "recognise",
      emoji: "🔎",
      title: "Recognise",
      blurb: "Common experiences described by people who relate to complex trauma.",
      intro:
        "This section describes common experiences — not a checklist. You are not being asked to diagnose yourself, only to notice, with curiosity, whatever feels true for you.",
      resources: [
        {
          slug: "emotional-flashbacks-and-triggers",
          kind: "learn",
          title: "Emotional flashbacks and triggers",
          summary: "Feeling flooded by an old feeling without an obvious cause.",
          minutes: 5,
          blocks: [
            {
              type: "prose",
              text: "An emotional flashback is a sudden, intense wave of feeling — often shame, fear, grief or panic — that echoes an earlier experience, without a clear visual memory attached. Unlike the flashbacks often shown in films, there may be no image at all, just an overwhelming feeling that seems to come from nowhere.",
            },
            {
              type: "list",
              title: "People sometimes describe",
              items: [
                "Feeling suddenly small, young, or powerless",
                "A rush of shame or fear that feels far bigger than the moment calls for",
                "An urge to hide, disappear, defend, or people-please, seemingly out of proportion",
                "Being told 'you're overreacting' and later wondering the same thing",
              ],
            },
            {
              type: "prose",
              text: "Triggers are the cues — a tone of voice, a smell, a type of silence, a specific phrase — that the nervous system has linked with past danger. They don't need to make logical sense to feel very real.",
            },
            {
              type: "note",
              text: "There's a dedicated Emotional Flashbacks module with more detail, grounding tools and a closer look at triggers, linked at the end of this page.",
            },
          ],
        },
        {
          slug: "hypervigilance-dissociation-shutdown",
          kind: "learn",
          title: "Hypervigilance, dissociation and shutdown",
          summary: "When the nervous system stays braced, or switches off altogether.",
          minutes: 5,
          blocks: [
            {
              type: "prose",
              text: "Hypervigilance is a state of heightened alertness — scanning rooms, reading faces, anticipating problems before they happen. It often develops when danger was unpredictable, so staying alert felt like the only way to stay safe.",
            },
            {
              type: "list",
              title: "Hypervigilance can look like",
              items: [
                "Struggling to relax even when nothing is currently wrong",
                "Startling easily at sudden noise or movement",
                "Constantly assessing other people's moods",
                "Difficulty sleeping because 'switching off' feels unsafe",
              ],
            },
            {
              type: "prose",
              text: "Dissociation and shutdown sit at the other end of the spectrum — a kind of protective disconnection from feelings, body sensations, memory or surroundings, which can develop when fighting or fleeing wasn't possible.",
            },
            {
              type: "list",
              title: "Dissociation and shutdown can look like",
              items: [
                "Feeling foggy, distant, or like you're watching yourself from outside",
                "Losing time, or gaps in memory for ordinary events",
                "Feeling numb or 'switched off' during emotionally significant moments",
                "A heavy, weighted stillness, or difficulty moving or speaking",
              ],
            },
            {
              type: "note",
              text: "The Dissociation and Window of Tolerance modules go into more depth on these states and gentle ways of working with them.",
            },
          ],
        },
        {
          slug: "emotional-regulation-difficulties",
          kind: "learn",
          title: "Difficulties with emotional regulation",
          summary: "Feelings that arrive fast, feel enormous, or feel completely absent.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "When a nervous system has adapted to frequent or prolonged threat, emotions can become harder to regulate — arriving suddenly and intensely, taking a long time to settle, or feeling muted and hard to access at all.",
            },
            {
              type: "list",
              title: "This is sometimes described as",
              items: [
                "Going from calm to overwhelmed with very little warning",
                "Feeling flooded by anger, panic or grief that's hard to bring back down",
                "Struggling to name what you're feeling, or feeling 'nothing' much of the time",
                "A sense that emotions are dangerous or embarrassing, and better avoided",
              ],
            },
            {
              type: "prose",
              text: "None of this reflects a lack of willpower or maturity. Emotional regulation is a skill that develops through co-regulation with safe others, especially in childhood — when that wasn't consistently available, regulating alone as an adult can understandably feel much harder.",
            },
          ],
        },
        {
          slug: "relationships-and-trust",
          kind: "learn",
          title: "Relationships and trust",
          summary: "Closeness that feels both longed for and frightening.",
          minutes: 5,
          blocks: [
            {
              type: "prose",
              text: "Complex trauma often develops in the context of relationships, so it's common for its effects to show up most clearly in relationships too — even ones that are safe and caring in the present.",
            },
            {
              type: "list",
              title: "People sometimes recognise",
              items: [
                "Longing for closeness, but pulling away once it's offered",
                "Assuming people will eventually leave, hurt, or be disappointed",
                "Difficulty trusting good treatment, or waiting for 'the catch'",
                "Over-giving, over-explaining, or shrinking to keep the peace",
                "Intense reactions to perceived rejection, even very small ones",
              ],
            },
            {
              type: "note",
              text: "The Trauma Bonds, Attachment Styles and Trauma & Relationships modules explore these patterns in far more depth, if any of this resonates.",
            },
          ],
        },
        {
          slug: "shame-and-self-worth",
          kind: "learn",
          title: "Shame and self-worth",
          summary: "When the belief 'something is wrong with me' feels like fact.",
          minutes: 5,
          blocks: [
            {
              type: "prose",
              text: "One of the more painful features of complex trauma is a persistent sense of being worthless, defeated, or fundamentally flawed — often carrying deep shame, sometimes about the trauma itself, even when none of it was the person's fault.",
            },
            {
              type: "list",
              title: "This can sound like",
              items: [
                "'If people really knew me, they'd leave'",
                "'I'm too much' or 'I'm not enough' — sometimes both, in the same day",
                "A harsh inner critic that seems to know exactly where it hurts",
                "Believing you don't deserve rest, help, or good things",
              ],
            },
            {
              type: "prose",
              text: "These beliefs usually formed as an explanation a person reached for at the time — 'it must be me' can feel more bearable to a child than 'the person I depend on isn't safe.' The belief can outlive its usefulness by decades.",
            },
            {
              type: "note",
              text: "The Shame-Based Trauma and Inner Critic modules focus specifically on this territory.",
            },
          ],
        },
      ],
    },
    {
      slug: "practise",
      emoji: "🌿",
      title: "Practise",
      blurb: "Grounded exercises to build awareness and a simple safety plan.",
      intro:
        "These exercises are optional, and you can stop at any point — stopping is a valid outcome, not a failure. None of them are designed to diagnose you; they're here to support gentle self-awareness and a felt sense of safety.",
      resources: [
        {
          slug: "what-do-i-recognise-in-myself",
          kind: "reflect",
          title: "What do I recognise in myself?",
          summary: "A gentle noticing exercise — awareness, not self-diagnosis.",
          minutes: 8,
          blocks: [
            {
              type: "note",
              text: "This is a reflection exercise, not a diagnostic tool. Recognising a pattern here doesn't confirm or rule out any diagnosis — only a qualified professional can do that. The aim is simply gentle self-awareness.",
            },
            {
              type: "prose",
              text: "Read back through the Recognise section slowly, without pressure to relate to all of it. Then use the prompts below to note what stood out, in your own words.",
            },
            {
              type: "prompts",
              title: "Reflection prompts",
              items: [
                "Which experiences described in this module felt familiar, if any?",
                "Which felt unfamiliar or didn't fit?",
                "Is there a pattern here I've noticed before but never had words for?",
                "How does it feel to see this written in plain, non-judgemental language?",
                "Is there anything I'd like to explore further — alone, in the journal, or with a professional?",
              ],
            },
            {
              type: "note",
              text: "If this brought up a lot, it's worth pausing here and returning to the Practise section's grounding tools, or to /grounding or /breathing, before continuing.",
            },
          ],
        },
        {
          slug: "grounding-first-safety-plan",
          kind: "practice",
          title: "A grounding-first safety plan",
          summary: "A simple, personal plan for moments of overwhelm.",
          minutes: 10,
          blocks: [
            {
              type: "prose",
              text: "A safety plan is a short, personal reference you build in a calm moment, so that it's ready to use in a harder one. This version starts with grounding — bringing attention back to the present and the body — before moving to wider supports.",
            },
            {
              type: "steps",
              title: "Building your plan",
              items: [
                { label: "Notice the signs", note: "What tends to tell you a wave, flashback or shutdown is starting? Racing heart, going quiet, a specific thought, feeling far away." },
                { label: "Choose one grounding action", note: "Something simple and always available — naming five things you can see, holding something cold, feeling your feet on the floor." },
                { label: "Choose one soothing action", note: "A phrase, a piece of music, a photo, a scent, a blanket — something that reminds your body of the present." },
                { label: "Name one safe person", note: "Someone you could message or call, even just to say 'having a hard moment.'" },
                { label: "Know your professional supports", note: "GP, therapist, or helpline numbers, written down somewhere you'll actually find them." },
                { label: "Know your crisis option", note: "If things ever feel unsafe, our Crisis Resources page lists urgent support options." },
              ],
            },
            {
              type: "list",
              title: "Grounding ideas to draw from",
              items: [
                "5-4-3-2-1: five things you see, four you hear, three you feel, two you smell, one you taste",
                "Pressing feet firmly into the floor and noticing the support beneath you",
                "Holding an ice cube, or splashing cool water on your hands or face",
                "Naming the date, your age, and where you are out loud",
                "Slow, longer exhales than inhales, if breath-focused grounding feels okay for you",
              ],
            },
            {
              type: "note",
              text: "You can build and revisit your plan any time in the Grounding and Breathing tools. This exercise won't save automatically here — you may like to note your plan in your Journal so it's there when you need it.",
            },
          ],
        },
        {
          slug: "my-cptsd-awareness-worksheet",
          kind: "worksheet",
          title: "My C-PTSD awareness worksheet",
          summary: "A printable worksheet for gentle, ongoing self-awareness.",
          minutes: 12,
          blocks: [
            {
              type: "note",
              text: "This worksheet supports self-awareness and is not a diagnostic instrument. It can be a useful thing to bring to a conversation with a therapist or GP, if you'd like professional input.",
            },
            {
              type: "prose",
              text: "Print this page, or fill it in digitally, whenever it feels useful — before or after a therapy session, during a calm week, or whenever you want to check in with yourself.",
            },
          ],
          worksheetFields: [
            { label: "Today's date", lines: 1 },
            { label: "What's felt present for me lately (feelings, patterns, body sensations)", lines: 4 },
            { label: "Situations or cues that seemed to trigger a strong reaction", lines: 4 },
            { label: "What helped, even a little, when things felt hard", lines: 3 },
            { label: "What I might like support with, or want to raise with a professional", lines: 3 },
            { label: "One thing I want to remember about myself this week", lines: 2 },
          ],
        },
      ],
    },
    {
      slug: "reflect",
      emoji: "✍️",
      title: "Reflect",
      blurb: "Journal prompts on recovery, pacing and progress that isn't a straight line.",
      intro:
        "Recovery from complex trauma is rarely linear, and this section reflects that. Use these prompts in the Journal, or simply sit with them.",
      resources: [
        {
          slug: "recovery-is-not-linear",
          kind: "learn",
          title: "Recovery, and why progress isn't linear",
          summary: "Understanding setbacks, waves, and changing capacity.",
          minutes: 5,
          blocks: [
            {
              type: "prose",
              text: "Recovery from complex trauma tends to move in waves rather than a straight upward line. Periods of steadiness are often followed by harder stretches — sometimes triggered by something identifiable, sometimes not — before steadiness returns again, often at a slightly higher baseline than before.",
            },
            {
              type: "list",
              title: "Things that can affect capacity on any given day",
              items: [
                "Sleep, illness, hunger and general physical wellbeing",
                "Anniversaries, seasons, or dates linked to past experiences",
                "Life stress unrelated to trauma — work, money, relationships",
                "Reminders or triggers that arrive without warning",
                "Simply doing deeper trauma-related work, which can stir things up before it settles",
              ],
            },
            {
              type: "prose",
              text: "A harder day, week or month is not evidence that progress has been lost or that earlier work 'didn't work.' It's a normal feature of how healing tends to happen — closer to a spiral than a ladder, revisiting familiar territory with a bit more capacity and understanding each time.",
            },
            {
              type: "quote",
              text: "Healing is rarely a straight line. Most people find it closer to a spiral — returning to familiar ground, but a little more resourced each time.",
            },
            {
              type: "note",
              text: "We never promise a particular outcome or timeline here — everyone's path looks different, and that's completely normal. If setbacks feel frightening or unmanageable, please reach out for professional support, or visit our Crisis Resources if things feel urgent.",
            },
          ],
        },
        {
          slug: "reflecting-on-your-own-pace",
          kind: "reflect",
          title: "Reflecting on your own pace",
          summary: "Journal prompts on setbacks, capacity, and self-compassion.",
          minutes: 8,
          blocks: [
            {
              type: "prompts",
              title: "On setbacks",
              items: [
                "What has a harder day or week usually looked like for me?",
                "Looking back, is there a pattern to when harder periods tend to arrive?",
                "What have I believed about myself during a setback that I might question now?",
              ],
            },
            {
              type: "prompts",
              title: "On capacity",
              items: [
                "What does it feel like in my body when my capacity is low?",
                "What has genuinely helped me through a harder stretch before?",
                "Who or what could I lean on a little more, next time capacity dips?",
              ],
            },
            {
              type: "prompts",
              title: "On self-compassion",
              items: [
                "If a friend were moving through what I've moved through, what would I want to say to them?",
                "What would it mean to treat a setback as information, rather than failure?",
                "What is one small, kind thing I could do for myself today, regardless of how the day is going?",
              ],
            },
          ],
        },
      ],
    },
  ],
};
